import PageHeader from "@/components/PageHeader";
import { Heart, ExternalLink, Wrench, Wind, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const principles = [
  {
    icon: Wrench,
    title: "Built for the Field",
    desc: "Every tool is designed for tradesmen — pipe fitters, electricians, HVAC techs, and plumbers — who need quick, reliable answers on the job.",
  },
  {
    icon: HandHeart,
    title: "Free, No Strings",
    desc: "All tools are free to use, forever. No subscriptions, no paywalls, no accounts required. Install it and go.",
  },
  {
    icon: Heart,
    title: "Value 4 Value",
    desc: "If this app saves you time or helps you on a job, consider giving back what it's worth to you. That's the deal — you decide the value.",
  },
];

const donateLinks = [
  {
    label: "PayPal",
    icon: Heart,
    url: "https://paypal.me/tradestoolbelt",
    desc: "Send any amount directly",
  },
];

const Support = () => {
  const [thankYou, setThankYou] = useState(false);

  const handleDonateClick = (url: string) => {
    setThankYou(true);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="pb-24 animate-fade-in">
      <PageHeader title="💛 Value 4 Value" subtitle="Support Trades Toolbelt" />

      <div className="px-4 mt-4 space-y-8 max-w-lg mx-auto">
        {/* Mission Statement */}
        <section className="space-y-3">
          <div className="trade-card text-center space-y-3 py-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-1">
              <Heart className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-lg font-bold text-foreground">
              Built by tradesmen, for tradesmen.
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
              Trades Toolbelt is a free app for Mechanical, Electrical, and Plumbing professionals. 
              No ads, no tracking, no paywall. If it helps you on the job, 
              consider giving back what it's worth to you.
            </p>
          </div>
        </section>

        {/* Principles */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            How It Works
          </h2>
          <div className="space-y-3">
            {principles.map((p, i) => (
              <div key={i} className="trade-card flex items-start gap-3">
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 shrink-0 mt-0.5">
                  <p.icon className="w-4.5 h-4.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{p.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Donate Buttons */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Support the Project
          </h2>
          <div className="trade-card space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Every contribution — big or small — helps keep this app free, updated, and growing with new tools.
            </p>
            <div className="space-y-2">
              {donateLinks.map((link, i) => (
                <Button
                  key={i}
                  variant="outline"
                  className="w-full justify-between h-auto py-3 px-4"
                  onClick={() => handleDonateClick(link.url)}
                >
                  <div className="flex items-center gap-3">
                    <link.icon className="w-5 h-5 text-primary" />
                    <div className="text-left">
                      <div className="text-sm font-medium">{link.label}</div>
                      <div className="text-[11px] text-muted-foreground">{link.desc}</div>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Thank You Message */}
        {thankYou && (
          <section className="space-y-3 animate-fade-in">
            <div className="trade-card text-center py-5 border-primary/20 bg-primary/5">
              <p className="text-sm font-semibold text-primary">🙏 Thank you for your support!</p>
              <p className="text-xs text-muted-foreground mt-1">
                Every dollar helps keep this app free for every tradesman.
              </p>
            </div>
          </section>
        )}

        {/* Share */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Other Ways to Help
          </h2>
          <div className="trade-card space-y-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Spread the word.</span> Tell a coworker, share it with your crew, or recommend it to an apprentice. Word of mouth from the field is the best support there is.
            </p>
          </div>
        </section>

        <p className="text-center text-[10px] text-muted-foreground/50 pb-4">
          Trades Toolbelt — Built for the field, supported by the field.
        </p>
      </div>
    </div>
  );
};

export default Support;
